import * as React from 'react'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Typography,
} from '@mui/material'
import { SummaryTable } from '../../SummaryTable'
import { useSyncSummary } from './hooks'
import { syncDifferencesAtom } from './atoms'
import {
  formatStandardIdsFromArray,
  formatStandardIdsFromRecord,
} from './utility'

interface DisplaySyncDifferencesProps {
  treeId: string
  versionId: string
}

export function DisplaySyncDifferences({
  treeId,
  versionId,
}: DisplaySyncDifferencesProps) {
  const summaryRows = useSyncSummary(treeId, versionId)
  const syncDifferences = useAtomValue(syncDifferencesAtom)

  return (
    <Box>
      <SummaryTable rows={summaryRows} />
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Change details
        </Typography>
        {syncDifferences.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2 }}>
            No differences to display
          </Typography>
        ) : (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: '300px',
              overflow: 'auto',
            }}
          >
            <Table size="small" stickyHeader sx={{ width: '100%' }}>
              <colgroup>
                <col style={{ width: 'auto', minWidth: '100px' }} />
                <col style={{ width: 'auto', minWidth: '150px' }} />
                <col style={{ width: 'auto', minWidth: '80px' }} />
                <col style={{ width: '100%' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: 'background.paper',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: 'background.paper',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    Title with parent title
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: 'background.paper',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      bgcolor: 'background.paper',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    Standard IDs
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncDifferences.map((diff, index) => {
                  const isNew = diff.new_skills_list
                  const treeIds = formatStandardIdsFromArray(
                    diff.tree_skills_list,
                  )
                  const sheetIds = formatStandardIdsFromRecord(
                    diff.sheet_skills,
                  )
                  const idsDisplay = isNew
                    ? sheetIds
                    : `${treeIds} -> ${sheetIds}`

                  // Determine status: new, deduplicated, changed, or title changed
                  let status = 'title'
                  if (isNew) {
                    status = 'new'
                  } else if (diff.deduplicated) {
                    status = 'deduplicated'
                  } else if (diff.skills_changed) {
                    status = 'changed'
                  }

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'grey.50',
                        },
                      }}
                    >
                      <TableCell>{diff.skills_list_parent_node_type}</TableCell>
                      <TableCell>
                        {diff.skills_list_title}
                        {diff.skills_list_parent_node_title && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: 'text.secondary',
                              mt: 0.5,
                            }}
                          >
                            {diff.skills_list_parent_node_title}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{status}</TableCell>
                      <TableCell
                        sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                      >
                        {idsDisplay}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  )
}
